// SPDX-FileCopyrightText: © 2024 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type { ComponentInterface, EventEmitter } from '@stencil/core'
import {
  AttachInternals,
  Component,
  Element,
  Event,
  h,
  Listen,
  Prop,
  State,
  Watch,
} from '@stencil/core'

import type { Attributes, QdsFocusEventDetail } from '../../helpers'
import { inheritAriaAttributes, pickFocusEventAttributes } from '../../helpers'
import { CUSTOM_ERROR_FLAGS, NO_ERROR_FLAGS, VALID_STATE } from '../controls'
import type { Size } from '../shared'

export type CheckboxValue = number | string | null

/**
 * `<qds-checkbox>` elements are rendered as boxes that are checked (ticked)
 * when activated, like you might see in an official government paper form. A
 * checkbox allows you to select single values for submission in a form (or not).
 *
 * @see https://quartz.se.com/build/components/checkbox
 */
@Component({
  tag: 'qds-checkbox',
  formAssociated: true,
  shadow: { delegatesFocus: true },
  styleUrl: 'checkbox.css',
})
export class Checkbox implements ComponentInterface {
  /**
   * Adds vertical margin to the checkbox for alignment.
   *
   * This is useful when creating inline layouts so that the first lines have
   * the correct vertical centering.
   */
  @Prop() public readonly inline: boolean = false

  /**
   * The checkbox's size.
   */
  @Prop() public readonly size?: Size = 'standard'

  /**
   * The checkbox's text.
   */
  @Prop() public readonly text?: string

  /**
   * Sets the checkbox's state.
   *
   * @webnative
   */
  @Prop({ mutable: true }) public checked?: boolean

  /**
   * Prevents the checkbox from being interacted with: it cannot be pressed or
   * focused.
   *
   * @webnative
   */
  @Prop() public readonly disabled?: boolean

  /**
   * The [`<form>`][] element to associate the checkbox with (its form owner).
   *
   * The value of this attribute must be the id of a `<form>` in the same
   * document. If this attribute is not set, the `<qds-checkbox>` is associated
   * with its ancestor `<form>` element, if any.
   *
   * This attribute lets you associate `<qds-checkbox>` elements to `<form>`s
   * anywhere in the document, not just inside a `<form>`. It can also override
   * an ancestor `<form>` element.
   *
   * [`<form>`]: https://developer.mozilla.org/docs/Web/HTML/Element/form
   *
   * @readonly
   * @webnative
   */
  // eslint-disable-next-line unicorn/no-null
  @Prop() public readonly form: ElementInternals['form'] | string = null

  /**
   * Sets the checkbox to an indeterminate state. This is usually applied to
   * checkboxes that represent a "select all/none" behavior when associated
   * checkboxes have a mix of checked and unchecked states.
   *
   * @webnative
   */
  @Prop({ mutable: true }) public indeterminate?: boolean

  /**
   * The name of the checkbox, which is submitted with the form data.
   *
   * @webnative
   */
  @Prop() public readonly name?: string

  /**
   * A value must be specified for the checkbox before the owning form can be
   * submitted.
   *
   * See
   * [Client-side validation](https://developer.mozilla.org/docs/Web/HTML/Element/input#client-side_validation)
   * and the
   * [HTML attribute: `required`](https://developer.mozilla.org/docs/Web/HTML/Attributes/required)
   * for more information.
   *
   * @webnative
   */
  @Prop() public readonly required?: boolean

  /**
   * The error message that would be shown to the user if the `<qds-checkbox>`
   * was to be checked for validity.
   *
   * @readonly
   * @webnative
   */
  @Prop()
  public readonly validationMessage: ElementInternals['validationMessage'] = ''

  /**
   * The [`ValidityState`][] object for this `<qds-checkbox>`.
   *
   * [`ValidityState`]: https://developer.mozilla.org/docs/Web/API/ValidityState
   *
   * @readonly
   * @webnative
   */
  @Prop() public readonly validity: ElementInternals['validity'] = VALID_STATE

  /**
   * The value of the checkbox, submitted as a name/value pair with form data.
   *
   * @webnative
   */
  @Prop() public readonly value?: CheckboxValue = 'on'

  /**
   * True if `<qds-checkbox>` will be validated when the form is submitted;
   * false otherwise.
   *
   * @readonly
   * @webnative
   */
  @Prop() public readonly willValidate: ElementInternals['willValidate'] = false

  @AttachInternals() private readonly internals!: ElementInternals

  /**
   * Emitted when the checkbox loses focus.
   */
  @Event({ eventName: 'qdsBlur', bubbles: false, cancelable: false })
  private readonly blurEmitter!: EventEmitter<QdsFocusEventDetail>

  /**
   * Emitted when a change to the checkbox's state is committed by the user.
   */
  @Event({ eventName: 'qdsChange', cancelable: false })
  private readonly changeEmitter!: EventEmitter<void>

  /**
   * Emitted when the checkbox gains focus.
   */
  @Event({ eventName: 'qdsFocus', bubbles: false, cancelable: false })
  private readonly focusEmitter!: EventEmitter<QdsFocusEventDetail>

  @Element() private readonly host!: HTMLElement

  @State() private tabIndex?: number

  #inheritedAttributes: Attributes = {}

  get #computedChecked(): boolean {
    return this.checked ?? false
  }

  get #computedDisabled(): boolean {
    return (
      (this.host.matches(':disabled') || (this.disabled ?? false)) &&
      this.host.getAttribute('disabled') !== 'false'
    )
  }

  get #computedIndeterminate(): boolean {
    return this.indeterminate ?? false
  }

  get #computedSize(): Size {
    switch (this.size) {
      case 'standard':
      case 'small':
      case 'large': {
        return this.size
      }
      default: {
        return 'standard'
      }
    }
  }

  get #computedValue(): string | null {
    // eslint-disable-next-line unicorn/no-null
    return this.value == null ? null : this.value.toString()
  }

  get #hasText(): boolean {
    return (
      (this.text !== undefined && this.text !== '') || this.required === true
    )
  }

  @Listen('click')
  protected onClick(event: MouseEvent): void {
    if (this.#computedDisabled) {
      event.stopImmediatePropagation()
      return
    }

    event.preventDefault()

    this.checked = this.#computedIndeterminate ? true : !this.#computedChecked
    this.changeEmitter.emit()
    this.indeterminate = false
  }

  @Watch('checked')
  protected checkedChanged(): void {
    this.internals.setFormValue(
      this.#computedChecked && !this.#computedDisabled
        ? this.#computedValue
        : // eslint-disable-next-line unicorn/no-null
          null,
    )
  }

  @Watch('disabled')
  protected disabledChanged(): void {
    if (this.#computedChecked && !this.#computedDisabled)
      this.internals.setFormValue(this.#computedValue)
    // eslint-disable-next-line unicorn/no-null
    else this.internals.setFormValue(null)
  }

  @Watch('tabindex')
  protected tabindexChanged(newValue: string): void {
    const parsedValue = Number.parseInt(newValue, 10)
    this.tabIndex =
      parsedValue === 0 || Number.isNaN(parsedValue) ? undefined : parsedValue
  }

  @Watch('value')
  protected valueChanged(): void {
    if (this.#computedChecked && !this.#computedDisabled)
      this.internals.setFormValue(this.#computedValue)
  }

  public componentWillLoad(): void {
    this.valueChanged()

    this.#defineGetter('form', () => this.internals.form)
    this.#defineGetter('willValidate', () => this.internals.willValidate)
    this.#defineGetter('validity', () => this.internals.validity)
    this.#defineGetter(
      'validationMessage',
      () => this.internals.validationMessage,
    )

    this.#inheritedAttributes = inheritAriaAttributes(this.host)
    this.tabindexChanged(this.host.getAttribute('tabindex') ?? '0')
  }

  public render() {
    return (
      // eslint-disable-next-line jsx-a11y/label-has-associated-control
      <label
        aria-disabled={this.#computedDisabled ? 'true' : undefined}
        class={{
          'qds-inline': this.inline,
          'qds-label': true,
        }}
        data-size={this.#computedSize}
      >
        <div class="qds-container">
          <input
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus={this.host.autofocus}
            checked={this.#computedIndeterminate ? false : this.checked}
            class="qds-checkbox"
            data-size={this.#computedSize}
            disabled={this.#computedDisabled}
            indeterminate={this.indeterminate}
            onBlur={this.#onBlur}
            onFocus={this.#onFocus}
            required={this.required}
            tabIndex={this.#computedDisabled ? undefined : this.tabIndex}
            type="checkbox"
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...this.#inheritedAttributes}
          />
          <qds-icon
            aria-hidden="true"
            class={{
              'qds-icon': true,
              'qds-checked': true,
            }}
            data-size={this.#computedSize}
            library="core"
            name="checked"
          />
          <div class="qds-box" data-size={this.#computedSize} />
          <qds-icon
            aria-hidden="true"
            class={{
              'qds-icon': true,
              'qds-indeterminate': true,
            }}
            data-size={this.#computedSize}
            library="core"
            name="indeterminate"
          />
        </div>
        {this.#hasText && (
          <qds-label
            required={this.required}
            size={this.size}
            text={this.text}
          />
        )}
      </label>
    )
  }

  public checkValidity: ElementInternals['checkValidity'] = () =>
    this.internals.checkValidity()

  public reportValidity: ElementInternals['reportValidity'] = () =>
    this.internals.reportValidity()

  public setCustomValidity: HTMLInputElement['setCustomValidity'] = (error) => {
    if (error) this.internals.setValidity(CUSTOM_ERROR_FLAGS, error, this.host)
    else this.internals.setValidity(NO_ERROR_FLAGS)
  }

  readonly #onBlur = (event: FocusEvent): void => {
    this.blurEmitter.emit(pickFocusEventAttributes(event))
  }

  readonly #onFocus = (event: FocusEvent): void => {
    this.focusEmitter.emit(pickFocusEventAttributes(event))
  }

  #defineGetter(p: PropertyKey, get: () => unknown): void {
    Object.defineProperty(this.host, p, { enumerable: true, get })
  }
}
